import React, {useState} from 'react';
import PropTypes from 'prop-types';

import {Grid} from "@mui/material";

import GridContainer from "components/grid/gridContainer";
import GridItem from "components/grid/gridItem";

import RetailerOptionValueName from "./RetailerOptionValueName";
import MappingElementContainer from "./MappingElementContainer";
import MappingSeparator from "./MappingSeparator";
import OptionValueListButton from "./OptionValueListButton";
import OptionValueListButtonIcon from "./OptionValueListButtonIcon";
import OptionValueName from "./OptionValueName";
import MappingActionContainer from "./MappingActionContainer";
import OptionValueList from "./OptionValueList";

import canQuickCreateAttributes from "components/attributeQuickCreate/canQuickCreateAttributes";
import CreateOptionValueDialog from "components/attributeQuickCreate/CreateOptionValueDialog";
import QuickCreateDialogs from "components/attributeQuickCreate/QuickCreateDialogs";

import mapRetailerOptionValue from 'api/retailerOptionValues/mapping';

import {getTranslation} from "domain/helpers/translations";

MappingElement.propTypes = {
    retailerOptionValue: PropTypes.shape({
        value: PropTypes.shape({
            id: PropTypes.string,
            option_value_id: PropTypes.string,
            option_id: PropTypes.string,
        }),
        preview: PropTypes.arrayOf(PropTypes.string),
    }),
    optionValues: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string
        })
    ),
    attributeName: PropTypes.string,
    option: PropTypes.object,
    lastPosition: PropTypes.number,
    onMap: PropTypes.func,
    onOptionValueCreated: PropTypes.func,
};

// looks like <CardAttribute /> but retailerOption and category or gender are too different for a single component
function MappingElement({retailerOptionValue, optionValues, attributeName, option, lastPosition, onMap, onOptionValueCreated, ...props}) {

    const [isOptionValuesListOpen, setIsOptionValuesListOpen] = useState(false);
    const [selectedMapping, setSelectedMapping] = useState(null);
    const [pendingCreationValue, setPendingCreationValue] = useState(null);
    const [restoredInputValue, setRestoredInputValue] = useState('');

    function toggleOptionValuesList() {
        // a manual (re)open must start from a blank search, not a restored one
        setRestoredInputValue('');
        setIsOptionValuesListOpen(!isOptionValuesListOpen);
    }

    function closeOptionValuesList() {
        setIsOptionValuesListOpen(false);
    }

    function handleChange(value)  {
        setSelectedMapping(value.label)
        closeOptionValuesList();

        mapRetailerOptionValue(retailerOptionValue.value.id, value.value).then(() => {
            onMap(retailerOptionValue.value.id, retailerOptionValue.value['option_id']);
        })
    }

    function handleCreateOption(inputValue) {
        setPendingCreationValue(inputValue);
        closeOptionValuesList();
    }

    function handleCancelToList(restoredValue) {
        setRestoredInputValue(restoredValue);
        setIsOptionValuesListOpen(true);
    }

    function handleCreated(createdOptionValue, close) {
        // even if the mapping call fails, the value now exists and must show
        // up in the selects of the other rows
        onOptionValueCreated(createdOptionValue);

        // same path as a manual selection
        handleChange({
            label: getTranslation(createdOptionValue).name,
            value: createdOptionValue.id,
        });
        close();
    }

    const isMapped = !!retailerOptionValue.value.option_value_id;
    const isCreationAllowed = canQuickCreateAttributes();

    function getMappedOptionValue(optionValueId) {
        if (!!selectedMapping) {
            return selectedMapping;
        }

        if (isMapped) {
            const matchedOptionValue = optionValues.find(optionValue => optionValue.value === optionValueId);
            return matchedOptionValue ? matchedOptionValue.label : null
        }

        // retailerOptionValue not mapped
        return null;
    }

    return (
        <MappingElementContainer>
            <GridContainer>
                <GridItem xs={4} sm={4}>
                    <RetailerOptionValueName
                        retailerOptionValueId={retailerOptionValue.value.id}
                        retailerOptionValue={getTranslation(retailerOptionValue.value).name}
                    />
                </GridItem>
                <GridItem xs={2} sm={3}>
                    <MappingSeparator isMapped={isMapped} links={retailerOptionValue.preview} />
                </GridItem>
                <GridItem xs={6} sm={5}>
                    <MappingActionContainer>
                        <Grid
                            item
                            xs={9}
                            sm={9}
                            sx={{
                                paddingLeft: '16px',
                                paddingRight: '16px',
                                alignSelf: 'center',
                            }}
                        >
                            {isOptionValuesListOpen ? (
                                <OptionValueList
                                    id={retailerOptionValue.value.id}
                                    valuesList={optionValues}
                                    defaultInputValue={restoredInputValue}
                                    onChange={(value) => handleChange(value)}
                                    onBlur={() => closeOptionValuesList()}
                                    onCreateOption={isCreationAllowed ? (inputValue) => handleCreateOption(inputValue) : undefined}
                                />
                            ) : (
                                <OptionValueName name={getMappedOptionValue(retailerOptionValue.value.option_value_id)} />
                            )}
                        </Grid>
                        <GridItem xs={3} sm={3}>
                            <OptionValueListButton onClick={() => toggleOptionValuesList()}>
                                <OptionValueListButtonIcon open={isOptionValuesListOpen} />
                            </OptionValueListButton>
                        </GridItem>
                    </MappingActionContainer>
                </GridItem>
            </GridContainer>
            <QuickCreateDialogs
                value={pendingCreationValue}
                tabName={attributeName}
                onCancelToList={(restoredValue) => handleCancelToList(restoredValue)}
                onEnd={() => setPendingCreationValue(null)}
                renderCreateDialog={({isOpen, initialName, close}) => (
                    <CreateOptionValueDialog
                        isOpen={isOpen}
                        option={option}
                        lastPosition={lastPosition}
                        initialName={initialName}
                        onCreated={(createdOptionValue) => handleCreated(createdOptionValue, close)}
                        onClose={close}
                    />
                )}
            />
        </MappingElementContainer>
    );
}

export default MappingElement;
